import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { PaymentDeviceDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { formatDeviceDate, longRentalReminderKey, todayInIsrael } from '@/helpers/paymentDevices';

const wasShownToday = (key: string): boolean => {
  try {
    return localStorage.getItem(key) != null;
  } catch {
    return false;
  }
};

const markShown = (key: string) => {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // No storage (private window): the reminder just shows again next time.
  }
};

/**
 * Once a day, the devices in a continuous rental of 3 months or more (HAT-1434), so the office
 * follows up with the projects. Dismissed per browser for the rest of the Israel day.
 */
const LongRentalReminder = ({ devices }: { devices: PaymentDeviceDtoIn[] }) => {
  const { t } = useTranslation();
  const key = longRentalReminderKey(todayInIsrael());
  const [isDismissed, setIsDismissed] = useState(() => wasShownToday(key));

  if (isDismissed || devices.length === 0) return null;

  const dismiss = () => {
    markShown(key);
    setIsDismissed(true);
  };

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => { if (!open) dismiss(); }}
      title={t('paymentDevices.longRental.reminder.title', { count: devices.length })}
      message={t('paymentDevices.longRental.reminder.message')}
      confirmText="paymentDevices.longRental.reminder.ok"
      onConfirm={dismiss}
      hideCancel
    >
      <div className="max-h-[320px] overflow-y-auto w-full self-stretch">
        <table className="w-full self-stretch body-sm-regular">
          <thead>
            <tr className="text-(--font-secondary) text-start">
              <th className="text-start py-2 px-3 whitespace-nowrap">{t('paymentDevices.columns.deviceNumber')}</th>
              <th className="text-start py-2 px-3 whitespace-nowrap">{t('paymentDevices.columns.fund')}</th>
              <th className="text-start py-2 px-3 whitespace-nowrap">{t('paymentDevices.columns.handedOverDate')}</th>
              <th className="text-start py-2 px-3 whitespace-nowrap">{t('paymentDevices.longRental.reminder.months')}</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => {
              const rental = device.currentRental;
              return (
                <tr key={device.id} className="border-t border-(--border-main)">
                  <td className="py-2 px-3 whitespace-nowrap"><bdi>{device.deviceNumber}</bdi></td>
                  <td className="py-2 px-3">{rental ? `${rental.fundId} | ${rental.fundName ?? ''}` : '—'}</td>
                  <td className="py-2 px-3">{formatDeviceDate(rental?.handedOverDate) || '—'}</td>
                  <td className="py-2 px-3">{rental?.rentalMonths ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ConfirmationDialog>
  );
};

export default LongRentalReminder;
